import {
  Matrix4,
  OrthographicCamera,
  PerspectiveCamera,
  Plane,
  Ray,
  Raycaster,
  Vector2,
  Vector3,
} from 'three';

import { closestPointInNavMesh } from '@spazious/dollhouse-navmesh';
import { isTouchEvent } from '@spazious/dollhouse-utils';

import { World } from '../../math/index.js';

import CameraControlsBase from './CameraControlsBase.js';

const _v3A = new Vector3();
const _v3B = new Vector3();
const _v3C = new Vector3();
const _ray = new Ray();
const _rotationMatrix = new Matrix4();
const _raycaster = new Raycaster();
const _normalizedCursor = new Vector2();

export class CameraControls extends CameraControlsBase {
  private _world?: World;
  private _view: 'orbit' | 'zenithal' | 'walk' = 'orbit';
  private _eyeLevel = 1.5;
  private walkTest?: (cursor: Vector2) => boolean;

  public orbitFromCursor = false;

  get target(): Vector3 {
    return this._target;
  }

  get view() {
    return this._view;
  }

  set view(view: 'orbit' | 'zenithal' | 'walk') {
    const prevView = this._view;
    this._view = view;
    if (prevView !== view) {
      this._updateViewState();
    }
  }

  get world() {
    return this._world;
  }

  set world(world: World | undefined) {
    const prevWorld = this._world;
    this._world = world;
    if (prevWorld !== world) {
      this._updateViewState();
    }
  }

  constructor(camera: PerspectiveCamera | OrthographicCamera, domElement?: HTMLElement) {
    super(camera, domElement);

    this.saveState();
    this._updateViewState();
  }

  private async _updateViewState() {
    this.dollyToCursor = true;
    this.maxSpeed = Infinity;
    this.dollySpeed = 1.0;
    this.azimuthRotateSpeed = 1.0;
    this.polarRotateSpeed = 1.0;
    this.minZoom = 0.01;
    this.maxZoom = Infinity;
    this.minDistance = Number.EPSILON;
    this.maxDistance = this._world?.sphere
      ? this.getDistanceToFitSphere(this._world.sphere.radius * 1.5)
      : Infinity;
    this.truckSpeed = 1.0;
    this.dollySpeed = 0.5;
    this.maxPolarAngle = Math.PI / 2;
    this.verticalDragToForward = true;
    // this.setBoundary();
    // this.boundaryEnclosesCamera = false;
    // this.boundaryFriction = 0.5;

    switch (this._view) {
      case 'orbit':
        this.mouseButtons.left = CameraControlsBase.ACTION.ROTATE;
        this.mouseButtons.wheel = CameraControlsBase.ACTION.DOLLY;
        this.touches.one = CameraControlsBase.ACTION.TOUCH_ROTATE;
        this.truckSpeed = 2;
        if (this._world) {
          await Promise.all([
            this.rotateTo(Math.PI / 4, Math.PI / 4, true),
            this.fitToSphere(this._world.sphere, true),
          ]);
        }
        break;
      case 'zenithal':
        this.mouseButtons.left = CameraControlsBase.ACTION.OFFSET;
        this.mouseButtons.wheel = CameraControlsBase.ACTION.DOLLY;
        this.touches.one = CameraControlsBase.ACTION.NONE;
        this.truckSpeed = 2;
        // if (this._world) {
        //   this.boundaryEnclosesCamera = true;
        //   this.setBoundary(
        //     this._world.bounds.applyMatrix4(new Matrix4().makeScale(1.1, Infinity, 1.1))
        //   );
        // }
        if (this._world) {
          await Promise.all([
            this.rotateTo(0, 0, true),
            this.fitToBox(this._world.bounds, true, {
              paddingLeft: 1,
              paddingRight: 1,
              paddingBottom: 1,
              paddingTop: 1,
            }),
          ]);
        }
        break;
      case 'walk':
        this.maxPolarAngle = Math.PI;
        this.azimuthRotateSpeed = this.polarRotateSpeed = -0.3;
        this.truckSpeed = 10.0;
        this.mouseButtons.left = CameraControlsBase.ACTION.ROTATE;
        this.mouseButtons.wheel = CameraControlsBase.ACTION.ZOOM;
        this.touches.one = CameraControlsBase.ACTION.TOUCH_ROTATE;
        this.minZoom = 1;
        this.maxZoom = 2;
        this.minDistance = this.maxDistance = Number.EPSILON;
        this.smoothTime = 0.3;
        await this.setLookAt(0, this._eyeLevel, 1, 0, this._eyeLevel, -1, true);
        break;
    }
    this.saveState();
  }

  protected _collisionTest() {
    let distance = Infinity;

    if (!this._world?.collider || this._view !== 'walk') return distance;

    const direction = _v3A.setFromSpherical(this._spherical).multiplyScalar(0.01);
    _rotationMatrix.lookAt(this._target, direction, this._camera.up);

    for (let i = 0; i < 4; i++) {
      const nearPlaneCorner = _v3B.copy(this._nearPlaneCorners[i]);
      nearPlaneCorner.applyMatrix4(_rotationMatrix);

      const origin = _v3C.addVectors(this._target, nearPlaneCorner);
      _ray.set(origin, direction);

      const intersect = this._world.collider.rayIntersect(_ray);

      if (intersect && intersect.distance < distance) {
        distance = intersect.distance;
      }
    }

    return distance;
  }

  public getNormalizedCursor(evt: PointerEvent): Vector2 {
    if (!this._domElement) {
      return new Vector2(0, 0);
    }

    const elem = this._domElement;
    const { clientX, clientY } = isTouchEvent(evt) ? evt.changedTouches[0] : evt;

    return _normalizedCursor.fromArray([
      ((clientX - elem.clientLeft) / elem.clientWidth) * 2.0 - 1.0,
      -((clientY - elem.clientTop) / elem.clientHeight) * 2.0 + 1.0,
    ]);
  }

  public resetViewState = (): void => {
    this._updateViewState();
  };

  protected _internalCursorWalk = (cursor: Vector2): void => {
    if (!this._enabled) return;
    if (this.walkTest?.(cursor) === false) return;

    const navMeshQuery = this._world?.navMeshQuery;
    if (!navMeshQuery) {
      return;
    }

    _raycaster.setFromCamera(cursor, this.camera);

    const planeIntersection = new Vector3();
    const plane = new Plane().setFromNormalAndCoplanarPoint(this._camera.up, new Vector3(0, 0, 0));

    if (!_raycaster.ray.intersectPlane(plane, planeIntersection)) {
      return;
    }

    const { point: navMeshQueryPoint } = closestPointInNavMesh(planeIntersection, navMeshQuery, {
      distance: 10000,
    });
    const isOusideNavMesh =
      navMeshQueryPoint.x === 0 && navMeshQueryPoint.y === 0 && navMeshQueryPoint.z === 0;

    if (isOusideNavMesh) {
      return;
    }

    const navMeshPoint = new Vector3(navMeshQueryPoint.x, navMeshQueryPoint.y, navMeshQueryPoint.z);
    // TODO Inplement crowd agent to avoid walking through obstacles
    const { x, z, y } = navMeshPoint;
    this.moveTo(x, y + this._eyeLevel, z, true);
  };

  protected _onEventDown = (evtDown: PointerEvent): void => {
    const walkDragginThreshold = 5;

    const stopDragging = (evtUp: PointerEvent) => {
      const cursor = this.getNormalizedCursor(evtUp);
      if (this._view === 'walk') {
        const diffX = evtDown.clientX - evtUp.clientX;
        const diffY = evtDown.clientY - evtUp.clientY;
        if (Math.abs(diffX) < walkDragginThreshold && Math.abs(diffY) < walkDragginThreshold) {
          this._internalCursorWalk(cursor);
        }
      }
      this._domElement?.ownerDocument.removeEventListener('pointerup', stopDragging);
    };

    this._domElement?.ownerDocument.addEventListener('pointerup', stopDragging);
    // if (!this._domElement || this._view !== '3d' || !this._world) {
    //   return;
    // }

    // const cursor = this.orbitFromCursor ? this.getNormalizedCursor(evt) : new Vector2(0, 0);

    // this.camera.updateMatrixWorld();
    // _raycaster.setFromCamera(cursor, this.camera);

    // const intersect = this._world.collider.rayIntersect(_raycaster.ray, FrontSide);
    // if (intersect) {
    //   this.setOrbitPoint(intersect.point.x, 0, intersect.point.z);
    // }
  };

  public connect(domElement: HTMLElement): void {
    super.connect(domElement);
    if (!this._domElement) {
      return;
    }
    this._domElement.addEventListener('pointerdown', this._onEventDown);
  }

  public disconnect(): void {
    super.disconnect();
    if (!this._domElement) {
      return;
    }
    this._domElement.removeEventListener('pointerdown', this._onEventDown);
  }
}

export default CameraControls;
